<template>
  <div class="tx-list-page">
    <v-container fluid grid-list-xl>
      <v-layout row wrap>
        <v-flex xs12>
          <div class="list-hero">
            <p class="list-crumb">Transit / Trámites para Reingreso</p>
            <h1 class="list-title">Trámites para Reingreso</h1>
            <p class="list-subtitle">
              Trámites rechazados a la espera de su reingreso. Al reingresarlos
              vuelven a Trámites para Procesar y se avisa al cliente; el motivo,
              los comprobantes y el historial del rechazo se conservan.
            </p>
          </div>
        </v-flex>

        <v-flex xs12>
          <tx-filter @search="onSearch" @download="onDownload"></tx-filter>
        </v-flex>

        <v-flex xs12>
          <v-card class="tx-card">
            <div class="tx-toolbar">
              <div class="tx-toolbar__info">
                <span class="tx-chip">
                  <v-icon small class="tx-chip__icon">restart_alt</v-icon>
                  {{ meta.totalGroups }} para reingreso
                  <template v-if="meta.totalInvoices !== meta.totalGroups">
                    ({{ meta.totalInvoices }} facturas)
                  </template>
                </span>
              </div>
            </div>
            <v-divider></v-divider>

            <v-card-text class="pa-0">
              <div class="table-wrap">
                <table class="tx-table">
                  <thead>
                    <tr>
                      <th># Factura</th>
                      <th>Distribuidor</th>
                      <th>Laboratorio</th>
                      <th>Órgano Anuente</th>
                      <th>Fecha de Ingreso</th>
                      <th>Control de Trámite</th>
                      <th>Fecha de Rechazo</th>
                      <th class="th-center">TAT de Rechazo</th>
                      <th>Reingreso Anterior</th>
                      <th>Control Anterior</th>
                      <th class="th-center">Detalles</th>
                    </tr>
                  </thead>
                  <!-- Un <tbody> por trámite, como en Contabilidad: la banda
                       con el consecutivo, sus facturas y, al final, el motivo
                       del rechazo. El grupo entero se realza junto. -->
                  <tbody
                    v-for="group in groups"
                    :key="group.key"
                    class="tx-group"
                  >
                    <!-- Banda del trámite con Reingresar, el mismo botón de
                         Rechazadas: el trámite rechazado se ve en las dos
                         pantallas y se reingresa desde cualquiera. Vuelve a
                         Trámites para Procesar y avisa al cliente por correo
                         (TRÁMITES REINGRESADOS). -->
                    <tr
                      class="tx-group-row"
                      :class="{ 'tx-group-row--single': !esConsolidado(group) }"
                    >
                      <td colspan="10">
                        <v-icon small class="tx-group-row__icon">{{
                          esConsolidado(group) ? 'layers' : 'description'
                        }}</v-icon>
                        {{ etiquetaTramite(group) }}
                        <strong>{{ consecutivoDe(group) }}</strong>
                        &middot; {{ facturasDe(group) }}
                        <span
                          v-if="esConsolidado(group)"
                          class="tx-group-row__muted"
                        >
                          &middot; CIF total {{ money(cifTramiteDe(group)) }}
                        </span>
                        <span
                          v-if="controlFdDe(group)"
                          class="tx-group-row__muted"
                        >
                          &middot; Control F&amp;D: {{ controlFdDe(group) }}
                        </span>
                      </td>
                      <td class="th-center">
                        <v-tooltip bottom>
                          <template slot="activator">
                            <button
                              class="tx-btn--group tx-btn--group-reinit"
                              :aria-label="reingresarLabel(group)"
                              @click="reinit(group)"
                            >
                              <v-icon small>restart_alt</v-icon>
                              Reingresar
                              <span
                                v-if="esConsolidado(group)"
                                class="tx-btn__count"
                              >
                                {{ group.invoices.length }}
                              </span>
                            </button>
                          </template>
                          <span>{{ reingresarHint(group) }}</span>
                        </v-tooltip>
                      </td>
                    </tr>

                    <tr
                      v-for="(item, index) in group.invoices"
                      :key="item.id"
                      :class="{ 'tx-child-row': esConsolidado(group) }"
                    >
                      <td class="tx-cell--strong tx-cell--factura">
                        {{ item.invoiceNumber || '—' }}
                      </td>
                      <td>{{ distName(item) }}</td>
                      <td>{{ labName(item) }}</td>
                      <td>
                        <div
                          v-if="
                            item.organosAnuentes && item.organosAnuentes.length
                          "
                          class="organo-list"
                        >
                          <span
                            v-for="o in item.organosAnuentes"
                            :key="o.id"
                            class="organo-tag"
                          >
                            {{ o.name }}
                          </span>
                        </div>
                        <span v-else class="tx-muted">—</span>
                      </td>
                      <td class="tx-nowrap">
                        {{ dateOf(item.admissionDate) }}
                      </td>
                      <td>{{ item.fdcontrolNumber || '—' }}</td>
                      <td class="tx-nowrap">
                        {{ dateOf(item.deniedDate) }}
                      </td>
                      <td class="th-center">
                        <span
                          class="tx-days"
                          title="Días hábiles desde el ingreso a F&D hasta el rechazo"
                        >
                          {{ item.total_fda >= 0 ? item.total_fda : '—' }}
                          <small>días</small>
                        </span>
                      </td>
                      <!-- Bitácora de la vuelta anterior, si el trámite ya
                           se había reingresado antes y volvió a rechazarse. -->
                      <td
                        class="tx-nowrap tx-muted"
                        title="Si ya se había reingresado antes, cuándo fue"
                      >
                        {{ dateOf(item.reinitDate) }}
                      </td>
                      <td
                        class="tx-muted"
                        title="Control F&D con el que se rechazó la vuelta anterior"
                      >
                        {{ item.previousFdControl || '—' }}
                      </td>
                      <!-- Una sola vez por trámite: en un consolidado la
                           celda abarca todas sus facturas y el ojo las muestra
                           todas juntas. -->
                      <td
                        v-if="celdaTramite(group, index)"
                        :rowspan="filasTramite(group)"
                        class="th-center"
                        :class="{ 'tx-cell--tramite': esConsolidado(group) }"
                      >
                        <v-tooltip bottom>
                          <template slot="activator">
                            <v-btn
                              class="btn-detail"
                              icon
                              small
                              aria-label="Ver detalles"
                              @click="handleDetails(group)"
                            >
                              <v-icon small>visibility</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ detallesHint(group) }}</span>
                        </v-tooltip>
                      </td>
                    </tr>

                    <!-- Motivo del rechazo, uno por trámite: se escribe y se
                         adjunta el comprobante sin salir del listado, igual
                         que en Rechazadas. En un consolidado vale para todas
                         sus facturas. -->
                    <tr
                      class="tx-reason-row"
                      :class="{ 'tx-child-row': esConsolidado(group) }"
                    >
                      <td colspan="11">
                        <div class="reason">
                          <span class="reason__label">
                            Motivo del rechazo
                          </span>
                          <input
                            type="text"
                            class="reason__input"
                            placeholder="Escribe por qué fue rechazado…"
                            :value="motivoDe(group)"
                            @change="saveReason(group, $event.target.value)"
                          />
                          <v-tooltip bottom>
                            <template slot="activator">
                              <button
                                class="reason__btn"
                                @click="openReasonFiles(group)"
                              >
                                <v-icon small>attach_file</v-icon>
                                Comprobante
                                <span
                                  v-if="docCount(group) > 0"
                                  class="reason__count"
                                >
                                  {{ docCount(group) }}
                                </span>
                              </button>
                            </template>
                            <span>
                              {{
                                docCount(group)
                                  ? `Ver el expediente y adjuntar más soportes (${docCount(
                                      group
                                    )} cargados)`
                                  : 'Adjuntar el comprobante del rechazo. Admite cualquier formato'
                              }}
                            </span>
                          </v-tooltip>
                        </div>
                      </td>
                    </tr>
                  </tbody>

                  <tbody v-if="!pagination.loading && groups.length === 0">
                    <tr>
                      <td colspan="11" class="empty-state">
                        No hay trámites para reingreso con los filtros
                        aplicados.
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-if="pagination.loading">
                    <tr>
                      <td colspan="11" class="empty-state">Cargando...</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>

        <v-flex xs12 v-if="pagination.pages">
          <div class="list-footer">
            <div class="list-footer__count">
              Mostrando {{ groups.length }} de {{ meta.totalGroups }} trámites
              ({{ meta.totalInvoices }}
              facturas)
            </div>
            <v-pagination
              v-model="pagination.page"
              :length="pagination.pages"
              :total-visible="7"
              color="#254f87"
              @input="asyncPagination"
            ></v-pagination>
          </div>
        </v-flex>
      </v-layout>
    </v-container>

    <v-dialog
      max-width="1024"
      v-if="detail.data || detail.items.length"
      v-model="detail.show"
    >
      <compose
        :key="detail.key"
        :item="detail.data"
        :items="detail.items"
        :code="detail.code"
        v-on:close-detail="detail.show = false"
      ></compose>
    </v-dialog>

    <v-dialog max-width="560" v-if="reason.data" v-model="reason.show">
      <reason-files
        :key="reason.key"
        :item="reason.data"
        v-on:close-reason="reason.show = false"
        v-on:save-reason="saveReasonFiles"
      ></reason-files>
    </v-dialog>

    <v-dialog max-width="350" persistent v-model="loading">
      <loading></loading>
    </v-dialog>

    <v-snackbar
      v-model="snack.show"
      :timeout="4000"
      :color="snack.color"
      right
      top
    >
      {{ snack.message }}
      <v-btn flat dark @click="snack.show = false">Cerrar</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import moment from 'moment-timezone'
import compose from './../dialog/DialogDetails'
import loading from './../dialog/DialogLoading'
import reasonFiles from './../dialog/DialogDeniedReason'
import TxFilter from './TransitFilter.vue'
import TramiteBand from './mixins/TramiteBand'
import TramiteRechazado from './mixins/TramiteRechazado'
import SessionHelper from '@/helpers/SessionHelper'

export default {
  mixins: [TramiteBand, TramiteRechazado],
  components: {
    compose,
    loading,
    'reason-files': reasonFiles,
    'tx-filter': TxFilter,
  },
  data() {
    return {
      loading: true,
      // El backend agrupa por consolidado, igual que el listado de facturas.
      groups: [],
      meta: { totalGroups: 0, totalInvoices: 0 },
      pagination: {
        page: 1,
        rpp: this.$config.resultsPerPage,
        totalItems: 0,
        loading: true,
        pages: 0,
        search: {},
      },
      detail: { show: null, data: null, items: [], code: '', key: 0 },
      reason: { show: null, data: null, key: 0 },
      snack: { show: false, color: 'info', message: '' },
    }
  },
  created() {
    this.asyncPagination()
  },
  computed: {
    invoices() {
      return this.groups.reduce((acc, g) => acc.concat(g.invoices), [])
    },
    pageIds() {
      return this.invoices.map(item => item.id)
    },
  },
  methods: {
    distName(item) {
      return item.distribuidor || '—'
    },
    labName(item) {
      return item.laboratorio || '—'
    },
    dateOf(value) {
      return value ? moment(value).format('DD/MM/YYYY') : '—'
    },
    notify(message, color = 'info') {
      this.snack = { show: true, color, message }
    },
    helperPaginate(response) {
      this.groups = (response && response.groups) || []
      this.meta = {
        totalGroups: (response && response.totalGroups) || 0,
        totalInvoices: (response && response.totalInvoices) || 0,
      }
      // Se pagina por GRUPO: un consolidado no queda partido entre páginas.
      this.pagination.totalItems = this.meta.totalInvoices
      this.pagination.pages =
        Math.ceil(this.meta.totalGroups / this.pagination.rpp) || 0
      this.loading = false
      this.pagination.loading = false
      this.loadDocCounts()
    },
    asyncPagination() {
      this.pagination.loading = true
      const filter = {
        rpp: this.pagination.rpp,
        page: this.pagination.page,
        // El mismo filtro de Rechazadas: las dos pantallas muestran los
        // mismos trámites rechazados.
        status: 'denied',
        ...this.pagination.search,
      }
      return new Promise(resolve => {
        this.$store
          .dispatch('invoices/getConsolidated', filter)
          .then(response => {
            this.helperPaginate(response)
            return resolve()
          })
      })
    },
    // ===== Motivo del rechazo =====
    saveReasonFiles() {
      this.reason.show = false
      this.loading = true
      this.asyncPagination().then(() => {
        this.notify('Comprobante agregado al expediente.', 'success')
      })
    },
    // ===== Otros =====
    onSearch(params) {
      Object.keys(params).forEach(key => {
        if (params[key] == null || params[key] === '') delete params[key]
      })
      this.pagination.search = params
      this.pagination.page = 1
      this.asyncPagination()
    },
    onDownload() {
      const filter = {
        status: 'denied',
        ...this.pagination.search,
        token: SessionHelper.token,
      }
      this.$socket.emit('exportTransitInvoices', filter)
    },
  },
}
</script>

<style scoped src="./transit-list.css"></style>

<style scoped>
/* Fila del motivo: cuelga del trámite, sin borde superior. */
.tx-reason-row td {
  border-top: none;
  padding-top: 0 !important;
  background: #fafbfc;
}
.reason {
  display: flex;
  align-items: center;
  gap: 8px;
}
.reason__label {
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #991b1b;
}
.reason__input {
  flex: 1;
  min-width: 0;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 5px 8px;
  font-size: 13px;
  color: #0f172a;
  background: #ffffff;
  transition: border-color 0.15s ease;
}
.reason__input:hover {
  border-color: #e2e8f0;
}
.reason__input:focus {
  outline: none;
  border-color: #254f87;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.12);
}
.reason__input::placeholder {
  color: #94a3b8;
}
.reason__btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 28px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid #fecaca;
  background: #ffffff;
  color: #b91c1c;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;
}
.reason__btn .v-icon {
  color: #b91c1c !important;
  font-size: 15px !important;
}
.reason__btn:hover {
  background: #fef2f2;
  border-color: #fca5a5;
}
.reason__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #fee2e2;
  font-size: 11px;
  font-weight: 700;
}
</style>
